import {
  DndContext,
  DragEndEvent,
  useDraggable,
  useDroppable,
} from '@dnd-kit/core';
import { Divider, noop } from '@mantine/core';
import { NAICS, naicsHierarchy } from '@scaleup/models';
import { IconGripVertical, IconX } from '@tabler/icons-react';
import React, { useEffect, useState } from 'react';
import { useSupplierIntakeContext } from './context';
import { Divide } from 'lucide-react';
import { set } from 'lodash';

type Item = { id: string; content: string; source: string };

const initialItems: Record<string, Item[]> = {
  area1: [
    { id: 'item1', content: 'Item 1', source: 'area1' },
    { id: 'item2', content: 'Item 2', source: 'area1' },
  ],
  area2: [
    { id: 'item3', content: 'Item 3', source: 'area2' },
    { id: 'item4', content: 'Item 4', source: 'area2' },
  ],
  area3: [
    { id: 'item5', content: 'Item 5', source: 'area3' },
    { id: 'item6', content: 'Item 6', source: 'area3' },
  ],
};

const getItems = () => {
  const items: Record<string, Item[]> = {};
  Array.from(
    Object.keys(naicsHierarchy) as Array<keyof typeof naicsHierarchy>
  ).forEach((k) => {
    const n = naicsHierarchy[k];
    Array.from(Object.keys(n) as Array<keyof typeof n>).forEach((key) => {
      const arr = n[key];
      // @ts-ignore
      items[key] = arr.map((it) => ({
        id: it.code,
        content: it.label,
        source: key,
      }));
    });
  });

  return items;
};

const ITEMS: Record<string, Item[]> = getItems();

const filterItems = (inputList: NAICS[]): Record<string, Item[]> => {
  const output: Record<string, Item[]> = {};

  // Iterate over each category in the farmData
  for (const category in ITEMS) {
    if (ITEMS.hasOwnProperty(category)) {
      // Filter items in the current category based on the input list
      const filteredItems = ITEMS[category]!.filter((item) =>
        inputList.some((inputItem) => inputItem.code === item.id)
      );

      // If there are any matching items, add them to the output
      if (filteredItems.length > 0) {
        output[category] = filteredItems;
      }
    }
  }

  return output;
};

const addItemById = (
  _data: Record<string, Item[]>,
  newItem: string
): Record<string, Item[]> => {
  const data = { ..._data };
  let matchedItem: Item | undefined;

  // Search through farmData to find an item with a matching id
  for (const category in ITEMS) {
    if (ITEMS.hasOwnProperty(category)) {
      matchedItem = ITEMS[category]!.find((item) => item.id === newItem);
      if (matchedItem) {
        break; // Exit the loop if a match is found
      }
    }
  }

  // If a match is found, add it to the correct category in the data object
  if (matchedItem) {
    const { source } = matchedItem;

    if (data[source]) {
      // If the category already exists, add the item to the array
      data[source].push(matchedItem);
    } else {
      // If the category does not exist, create a new array with the item
      data[source] = [matchedItem];
    }
  }

  return data;
};

const DraggableItem: React.FC<{ 
  item: Item, 
  itemRemove: (itemId: string) => void,
  disabled?: boolean
}> = ({ item, itemRemove, disabled }) => {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: item.id,
  });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      className="bg-primary-50 rounded-lg flex justify-between w-full p-2"
    >
      <div className="flex items-center gap-4">
        {!disabled && <div {...listeners} className="cursor-grab">
          <IconGripVertical color="#387d91" />
        </div>}
        <div className="flex flex-col gap-1">
          <p className="text-text-primary font-bold">{item.id}</p>
          <p className="text-text-primary text-sm">{item.content}</p>
        </div>
      </div>
      {!disabled && <div className="flex flex-col items-start">
        <button onClick={() => itemRemove(item.id)} className="p-1">
          <IconX color="#982c2b" width={15} />
        </button>
      </div>}
    </div>
  );
};

const DroppableArea: React.FC<{
  id: string;
  children?: React.ReactNode;
  allowDrop: boolean;
  placeholder?: string;
}> = ({ id, children, allowDrop, placeholder }) => {
  const { setNodeRef, isOver } = useDroppable({ id });
  const entry = placeholder ? placeholder : 'Drop Here';
  return (
    <div ref={setNodeRef} className="flex flex-col gap-2 w-full">
      {children || <p>{allowDrop ? entry : 'Full'}</p>}
    </div>
  );
};

export const NDrop: React.FC<{ setDirty?: (value: boolean) => void;
  disabled?: boolean
}> = ({setDirty, disabled}) => {
  const { supplier, naics, setNAICS, primaryNAICS, setPrimaryNAICS } = useSupplierIntakeContext();
  const [areas, setAreas] = useState<Record<string, Item[]>>(
    filterItems(naics || [])
  );
  const [primaryItem, setPrimaryItem] = useState<Item | null>(primaryNAICS ? {
    id: primaryNAICS.code,
    content: primaryNAICS.label,
    source: 'primary',
  } : null);

  useEffect(() => {
    setAreas(filterItems(naics || []));
  }, [naics]);

  const handleRemoveItem = (itemId: string) => {
    const newNAICS = naics.filter((n) => n.code !== itemId);
    setNAICS(newNAICS);
    setAreas(filterItems(naics || []));
    if (setDirty) setDirty(true);

    if (primaryItem?.id === itemId) {
      setPrimaryItem(null);
      setPrimaryNAICS(undefined);
    }
  }
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;

    const sourceArea = Object.keys(areas).find((key) =>
      (areas[key] ?? []).some((item) => item.id === active.id)
    );

    const activeItem = sourceArea
      ? areas[sourceArea]?.find((item) => item.id === active.id)
      : primaryItem;

    if (!activeItem) return;

    if (over.id === 'primary' && !primaryItem && sourceArea) {
      // Move item to area3
      setAreas((prev) => ({
        ...prev,
        ...(sourceArea
          ? {
              [sourceArea]: prev[sourceArea]?.filter((item) => item.id !== active.id) ?? [],
            }
          : {}),
      }));
      setPrimaryItem(activeItem);
      setPrimaryNAICS({ code: activeItem.id, label: activeItem.content, description: activeItem.content });
      if(setDirty) setDirty(true);
    } else if (over.id === activeItem.source) {
      // Move item back to its original source
      if (primaryItem?.id === active.id) {
        setAreas((prev) => ({
          ...prev,
          [activeItem.source]: [...(prev[activeItem.source] || []), primaryItem],
        }));
        setPrimaryItem(null);
        setPrimaryNAICS(undefined);
        if(setDirty) setDirty(true);
      }
    }
  };

  return (
    <DndContext onDragEnd={handleDragEnd}>
      <div className="flex flex-col gap-2 w-full">
        <div className='pb-[40px]'>
          <div>
              <p className="text-[#377D91] font-medium text-base leading-6">Primary NAICS Code</p>
            </div>
          <DroppableArea id="primary" allowDrop={!primaryItem && !disabled} placeholder='Drag and drop one code here to set it as your company’s primary code. This code represents your main business focus.'>
            {primaryItem && <DraggableItem disabled={disabled} item={primaryItem} itemRemove={handleRemoveItem} />}
          </DroppableArea>
        </div>
        <Divider color="#c6e7ef" orientation="horizontal" className="w-full mb-4" />
        {Object.keys(areas).map((area) => (
          <div key={area}>
            <h3 className='text-[#377D91] font-medium text-base leading-6'>{area}</h3>
            <DroppableArea id={area} allowDrop={true && !disabled}>
              {areas[area]?.map((item) => (
                <DraggableItem key={item.id} item={item} itemRemove={handleRemoveItem} disabled={disabled}/>
              ))}
            </DroppableArea>
            <Divider color="#c6e7ef" orientation="horizontal" className="w-full mb-4 mt-8" />
          </div>
        ))}
      </div>
    </DndContext>
  );
};
